/**
 * AnchorPicker — choose what an attachment anchors to.
 *
 * Two modes:
 *   • Project tree (default in project contexts) — pick a project, optionally
 *     narrow to a sub-anchor (part / batch / BG / inspection event)
 *   • Master entity (Patch 2) — anchor to a client, supplier, or master
 *     department, with no project at all
 *
 * The two modes are mutually exclusive. Switching mode wipes the anchor.
 *
 * Defaults: launched from a project surface → Project tree + project pre-filled
 * + Project-level sub-anchor. Launched from a master-entity surface → Master
 * mode + type + entity pre-filled. Launched from a cross-project surface (the
 * /communications page) → no defaults; user picks.
 *
 * Project listings (projects, parts, batches, BGs, inspections, clients,
 * suppliers, departments) are supplied as `EntityOption[]` by the parent.
 * The picker stays presentational.
 */
import type { ReactNode } from 'react';
import { useState } from 'react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import type { AttachmentAnchor } from '@/lib/attachments-queries';

export interface EntityOption {
  id: string;
  label: string;
}

interface Props {
  value: AttachmentAnchor;
  onChange: (next: AttachmentAnchor) => void;

  // -------- Project-tree mode --------
  /**
   * When set, the picker scopes to this project (no project-picker UI). When
   * null, the picker shows a project select in project-tree mode.
   */
  projectId?: string | null;
  /** Projects to choose from when projectId is null (cross-project surfaces). */
  projects?: EntityOption[];
  parts?: EntityOption[];
  batches?: EntityOption[];
  bankGuarantees?: EntityOption[];
  inspectionEvents?: EntityOption[];

  // -------- Master-entity mode (Patch 2) --------
  clients?: EntityOption[];
  suppliers?: EntityOption[];
  departments?: EntityOption[];

  /**
   * Restrict the picker to one mode. Defaults to allowing both. Set to
   * ['PROJECT'] on per-project surfaces (the Documents tab) where master
   * anchoring doesn't make sense.
   */
  allowedKinds?: ('PROJECT' | 'MASTER')[];
}

type AnchorKind = 'PROJECT' | 'MASTER';
type SubAnchorKey = 'NONE' | 'PART' | 'BATCH' | 'BG' | 'INSPECTION';
type MasterKey = 'CLIENT' | 'SUPPLIER' | 'DEPARTMENT';

function inferSubAnchor(anchor: AttachmentAnchor): SubAnchorKey {
  if (anchor.bankGuaranteeId) return 'BG';
  if (anchor.inspectionEventId) return 'INSPECTION';
  if (anchor.orderBatchId) return 'BATCH';
  if (anchor.projectItemId) return 'PART';
  return 'NONE';
}

const EMPTY_ANCHOR: AttachmentAnchor = {
  projectId: null,
  projectItemId: null,
  orderBatchId: null,
  bankGuaranteeId: null,
  inspectionEventId: null,
  clientId: null,
  supplierId: null,
  masterDepartmentId: null,
};

export function AnchorPicker({
  value,
  onChange,
  projectId,
  projects = [],
  parts = [],
  batches = [],
  bankGuarantees = [],
  inspectionEvents = [],
  clients = [],
  suppliers = [],
  departments = [],
  allowedKinds = ['PROJECT', 'MASTER'],
}: Props) {
  // Bug 4/5 fix — the picker's three layers of radio toggles (PROJECT/MASTER
  // outer, Part/Batch/BG/Inspection sub-anchor, Client/Supplier/Department
  // master-key) all originally derived their selection from which ID field
  // happened to be set in the anchor model. That works for "user has
  // already picked an entity," but breaks for "user just clicked the radio
  // and we haven't picked an entity yet" — the anchor stays empty, the
  // inferred kind defaults back to PROJECT, and the radio never sticks.
  // Each layer needs a `pending<X>` local state that captures the user's
  // selection intent; once an entity is picked the data-derived kind takes
  // over and pending<X> becomes redundant.
  const [pendingKind, setPendingKind] = useState<AnchorKind | null>(null);
  const [pendingSub, setPendingSub] = useState<SubAnchorKey | null>(null);
  const [pendingMaster, setPendingMaster] = useState<MasterKey | null>(null);

  // Kind: master IDs win, then projectId, else pending, default PROJECT.
  const kind: AnchorKind =
    value.clientId || value.supplierId || value.masterDepartmentId
      ? 'MASTER'
      : value.projectId
        ? 'PROJECT'
        : (pendingKind ?? 'PROJECT');

  const dataSub = inferSubAnchor(value);
  const sub: SubAnchorKey = dataSub !== 'NONE' ? dataSub : (pendingSub ?? 'NONE');

  const masterKey: MasterKey = value.supplierId
    ? 'SUPPLIER'
    : value.masterDepartmentId
      ? 'DEPARTMENT'
      : value.clientId
        ? 'CLIENT'
        : (pendingMaster ?? 'CLIENT');

  const setKind = (next: AnchorKind) => {
    if (next === kind) return;
    setPendingKind(next);
    setPendingSub(null);
    setPendingMaster(null);
    if (next === 'PROJECT') {
      onChange({ ...EMPTY_ANCHOR, projectId: projectId ?? null });
    } else {
      onChange({ ...EMPTY_ANCHOR });
    }
  };

  const setSub = (next: SubAnchorKey) => {
    onChange({
      ...EMPTY_ANCHOR,
      projectId: value.projectId ?? projectId ?? null,
    });
    setPendingSub(next === 'NONE' ? null : next);
  };

  const setMaster = (next: MasterKey) => {
    if (next === masterKey) return;
    onChange({ ...EMPTY_ANCHOR });
    setPendingMaster(next);
  };

  const showKindToggle = allowedKinds.length > 1;

  return (
    <fieldset className="space-y-2">
      <legend className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">
        Anchor
      </legend>

      {showKindToggle ? (
        <div className="flex items-center gap-2">
          <RadioChip
            checked={kind === 'PROJECT'}
            onChange={() => setKind('PROJECT')}
            label="Project"
          />
          <RadioChip
            checked={kind === 'MASTER'}
            onChange={() => setKind('MASTER')}
            label="Master entity"
          />
        </div>
      ) : null}

      {kind === 'PROJECT' ? (
        <div className="space-y-2">
          {projectId ? null : (
            <SearchableSelect
              items={projects.map((p) => ({ value: p.id, label: p.label }))}
              value={value.projectId}
              onChange={(v) => onChange({ ...EMPTY_ANCHOR, projectId: v })}
              placeholder="— pick a project —"
              clearable
            />
          )}

          <div className="text-xs text-secondary">
            Project-level by default. Narrow to a specific part, batch, BG, or inspection event below.
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <RadioChip
              checked={sub === 'NONE'}
              onChange={() => setSub('NONE')}
              label="Project-level"
            />
            <RadioChip
              checked={sub === 'PART'}
              onChange={() => setSub('PART')}
              label="Part"
              disabled={parts.length === 0}
            />
            <RadioChip
              checked={sub === 'BATCH'}
              onChange={() => setSub('BATCH')}
              label="Batch"
              disabled={batches.length === 0}
            />
            <RadioChip
              checked={sub === 'BG'}
              onChange={() => setSub('BG')}
              label="BG"
              disabled={bankGuarantees.length === 0}
            />
            <RadioChip
              checked={sub === 'INSPECTION'}
              onChange={() => setSub('INSPECTION')}
              label="Inspection"
              disabled={inspectionEvents.length === 0}
            />
          </div>

          {sub === 'PART' ? (
            <SearchableSelect
              items={parts.map((p) => ({ value: p.id, label: p.label }))}
              value={value.projectItemId}
              onChange={(v) =>
                onChange({
                  ...EMPTY_ANCHOR,
                  projectId: value.projectId ?? projectId ?? null,
                  projectItemId: v,
                })
              }
              placeholder="— pick a part —"
              clearable
            />
          ) : null}

          {sub === 'BATCH' ? (
            <SearchableSelect
              items={batches.map((b) => ({ value: b.id, label: b.label }))}
              value={value.orderBatchId}
              onChange={(v) =>
                onChange({
                  ...EMPTY_ANCHOR,
                  projectId: value.projectId ?? projectId ?? null,
                  orderBatchId: v,
                })
              }
              placeholder="— pick a batch —"
              clearable
            />
          ) : null}

          {sub === 'BG' ? (
            <SearchableSelect
              items={bankGuarantees.map((g) => ({ value: g.id, label: g.label }))}
              value={value.bankGuaranteeId}
              onChange={(v) =>
                onChange({
                  ...EMPTY_ANCHOR,
                  projectId: value.projectId ?? projectId ?? null,
                  bankGuaranteeId: v,
                })
              }
              placeholder="— pick a BG —"
              clearable
            />
          ) : null}

          {sub === 'INSPECTION' ? (
            <SearchableSelect
              items={inspectionEvents.map((i) => ({ value: i.id, label: i.label }))}
              value={value.inspectionEventId}
              onChange={(v) =>
                onChange({
                  ...EMPTY_ANCHOR,
                  projectId: value.projectId ?? projectId ?? null,
                  inspectionEventId: v,
                })
              }
              placeholder="— pick an inspection event —"
              clearable
            />
          ) : null}
        </div>
      ) : (
        // Master entity mode
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <RadioChip
              checked={masterKey === 'CLIENT'}
              onChange={() => setMaster('CLIENT')}
              label="Client"
            />
            <RadioChip
              checked={masterKey === 'SUPPLIER'}
              onChange={() => setMaster('SUPPLIER')}
              label="Supplier"
            />
            <RadioChip
              checked={masterKey === 'DEPARTMENT'}
              onChange={() => setMaster('DEPARTMENT')}
              label="Department"
            />
          </div>

          {masterKey === 'CLIENT' ? (
            <SearchableSelect
              items={clients.map((c) => ({ value: c.id, label: c.label }))}
              value={value.clientId}
              onChange={(v) => onChange({ ...EMPTY_ANCHOR, clientId: v })}
              placeholder="— pick a client —"
              clearable
            />
          ) : null}

          {masterKey === 'SUPPLIER' ? (
            <SearchableSelect
              items={suppliers.map((s) => ({ value: s.id, label: s.label }))}
              value={value.supplierId}
              onChange={(v) => onChange({ ...EMPTY_ANCHOR, supplierId: v })}
              placeholder="— pick a supplier —"
              clearable
            />
          ) : null}

          {masterKey === 'DEPARTMENT' ? (
            <SearchableSelect
              items={departments.map((d) => ({ value: d.id, label: d.label }))}
              value={value.masterDepartmentId}
              onChange={(v) => onChange({ ...EMPTY_ANCHOR, masterDepartmentId: v })}
              placeholder="— pick a department —"
              clearable
            />
          ) : null}
        </div>
      )}
    </fieldset>
  );
}

function RadioChip({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={
        // Patch 3 — mobile gets ≥40px tap targets; desktop stays dense.
        'inline-flex min-h-[40px] items-center gap-1.5 rounded-sm border px-3 py-2 text-sm transition-colors sm:min-h-0 sm:gap-1 sm:px-2 sm:py-1 sm:text-xs ' +
        (checked
          ? 'border-accent bg-accent/5 text-accent-strong'
          : disabled
            ? 'border-border-default text-tertiary opacity-60'
            : 'border-border-default text-secondary hover:border-border-strong hover:text-primary')
      }
    >
      <span
        aria-hidden
        className={
          'inline-block h-2 w-2 rounded-full border ' +
          (checked ? 'border-accent bg-accent' : 'border-tertiary')
        }
      />
      {label}
    </button>
  );
}
